import { fetchPromotionData } from "/app/src/routes/{-$locale}/tags/$id.tsx?tsr-shared=1";
import * as React from "react";
import { HydrationBoundary, keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCountry } from "@/hooks/use-country";
import { usePreferences } from "@/hooks/use-preferences";
import debounce from "lodash.debounce";
import { getImage } from "@/lib/get-image";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { ArrowDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { GridIcon, ListBulletIcon } from "@radix-ui/react-icons";
import { OfferCard } from "@/components/app/offer-card";
import { OfferListItem } from "@/components/app/game-card";
import { useTranslation } from "@/lib/paraglide-react";
type SortBy = "releaseDate" | "lastModifiedDate" | "effectiveDate" | "creationDate" | "viewableDate" | "pcReleaseDate" | "upcoming" | "price";
const sortByKeys: SortBy[] = ["releaseDate", "lastModifiedDate", "effectiveDate", "creationDate", "viewableDate", "pcReleaseDate", "upcoming", "price"];
import { Route } from "/app/src/routes/{-$locale}/tags/$id.tsx";
function RouteComponent() {
  const {
    t
  } = useTranslation();
  const {
    country
  } = useCountry();
  const {
    view,
    setView
  } = usePreferences();
  const [sortBy, setSortBy] = React.useState<SortBy>("lastModifiedDate");
  const [sortDir, setSortDir] = React.useState<"asc" | "desc">("desc");
  const [inputValue, setInputValue] = React.useState("");
  const [query, setQuery] = React.useState("");
  const debouncedSetQuery = debounce(setQuery, 500);
  const sortByDisplay: Record<SortBy, string> = {
    releaseDate: t("tags.sortBy.releaseDate"),
    lastModifiedDate: t("tags.sortBy.lastModifiedDate"),
    effectiveDate: t("tags.sortBy.effectiveDate"),
    creationDate: t("tags.sortBy.creationDate"),
    viewableDate: t("tags.sortBy.viewableDate"),
    pcReleaseDate: t("tags.sortBy.pcReleaseDate"),
    upcoming: t("tags.sortBy.upcoming"),
    price: t("tags.sortBy.price")
  };
  const {
    cover,
    id
  } = Route.useLoaderData() as {
    dehydratedState: DehydratedState;
    id: string;
    cover: Pick<SingleOffer, "_id" | "id" | "namespace" | "title" | "keyImages"> | null;
    promotion: {
      elements: SingleOffer[];
      title: string;
      start: number;
      page: number;
      count: number;
    } | null;
  };
  const {
    data: promotion,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching
  } = useInfiniteQuery({
    queryKey: ["promotion", {
      id,
      country,
      sortBy,
      sortDir,
      query,
      limit: 20
    }],
    queryFn: ({
      pageParam
    }) => fetchPromotionData({
      id,
      country,
      page: pageParam as number,
      sortBy,
      sortDir,
      query
    }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.start + 20 > lastPage.count) {
        return undefined;
      }
      return allPages?.length + 1;
    },
    placeholderData: keepPreviousData
  });
  if (isLoading) {
    return <div>{t("tags.loading")}</div>;
  }
  if (!promotion) {
    return null;
  }
  const promotionPages = promotion.pages?.filter(Boolean) ?? [];
  const firstPromotionPage = promotionPages[0];
  if (!firstPromotionPage) {
    return null;
  }
  const promotionOffers = promotionPages.flatMap(page => Array.isArray(page.elements) ? page.elements : []);
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    debouncedSetQuery(event.target.value);
  };
  return <main className="container mx-auto">
      <div className="relative flex h-72 items-center overflow-hidden rounded-2xl bg-cover bg-center md:h-96" style={{
      backgroundImage: `url(${getImage(cover?.keyImages ?? [], ["OfferImageWide", "featuredMedia", "DieselGameBoxWide", "DieselStoreFrontWide"])?.url ?? "/placeholder.webp"})`
    }}>
        <div className="flex h-full w-full flex-col items-start justify-center bg-gradient-to-r from-black/80 to-black/30 p-5 text-foreground md:p-8">
          <h1 className="text-3xl font-bold leading-tight md:text-5xl">
            {firstPromotionPage.title}
          </h1>
          <p className="mt-4 text-lg">
            {t("tags.offersCount", {
            count: firstPromotionPage.count
          })}
          </p>
        </div>
      </div>

      <header className="mt-5 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <div className="inline-flex flex-wrap items-center gap-2">
          <h2 className="text-2xl">{t("tags.results")}</h2>
          <span className="text-sm text-muted-foreground">
            {t("tags.resultsCount", {
            count: promotionOffers.length
          })}
          </span>
          {isFetching && <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>}
        </div>
        <div className="flex w-full flex-wrap gap-2 md:w-auto">
          <Input type="search" placeholder={t("tags.searchPlaceholder")} className="w-full cursor-text sm:w-[200px]" onChange={handleInputChange} value={inputValue} />
          <Select value={sortBy} onValueChange={value => setSortBy(value as SortBy)}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue className="text-sm">{sortByDisplay[sortBy]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {sortByKeys.map(key => <SelectItem key={key} value={key}>
                  {sortByDisplay[key]}
                </SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="h-9 w-9 p-0" onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}>
            <ArrowDownIcon className={cn("h-5 w-5 transform transition-transform", sortDir === "asc" ? "-rotate-180" : "rotate-0")} aria-hidden="true" />
          </Button>
          <Button variant="outline" className="h-9 w-9 p-0" onClick={() => setView(view === "grid" ? "list" : "grid")}>
            {view === "grid" ? <ListBulletIcon className="h-5 w-5" aria-hidden="true" /> : <GridIcon className="h-5 w-5" aria-hidden="true" />}
          </Button>
        </div>
      </header>
      <div className={cn("mt-8 grid gap-4", view === "grid" ? "grid-cols-1 lg:grid-cols-3 xl:grid-cols-5" : "grid-cols-1")}>
        {promotionOffers.map(game => view === "grid" ? <OfferCard offer={game} key={game.id} size="md" /> : <OfferListItem game={game} key={game.id} />)}
      </div>
      <div className="flex justify-center mt-8">
        <Button disabled={!hasNextPage} onClick={() => fetchNextPage()}>
          {isFetchingNextPage && <svg className="animate-spin -ml-1 mr-3 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>}
          {t("tags.loadMore")}
        </Button>
      </div>
    </main>;
}
const SplitComponent = () => {
  const {
    dehydratedState
  } = Route.useLoaderData() as {
    dehydratedState: DehydratedState;
    id: string;
    cover: Pick<SingleOffer, "_id" | "id" | "namespace" | "title" | "keyImages"> | null;
    promotion: {
      elements: SingleOffer[];
      title: string;
      start: number;
      page: number;
      count: number;
    } | null;
  };
  return <HydrationBoundary state={dehydratedState}>
        <RouteComponent />
      </HydrationBoundary>;
};
export { SplitComponent as component };